import _ from 'lodash';
import {
  TextCollectionField,
  TextCollectionFieldType,
} from '@scale/shared/types/params/textcollection';
import { TextCollectionAnnotation } from '@scale/shared/types/response/textcollection';
import {
  areAllFieldConditionsMet,
  isFieldConditionsCodeMet,
  flattenFields,
  flattenAnnotations,
  areTemplateVariableConditionsMet,
} from '@scale/shared/textcollection/helpers';
import { TextCollectionAnnotationsOrCallback } from '@scale/shared/types/textCollection';
import { getDefaultAnnotation } from '@scale/shared/textcollection/defaults';
import { useTemplateVariableStore } from 'react-scale/components/scalers/tasks/textcollection/stores/TemplateVariableStore';
import SimpleFieldSection from './SimpleFieldSection';
import { ChatTaskBehaviorLogMetadata } from '@scale/shared/types/chat';
import { ChatMessageDisplayConfig } from '../../ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import { useState } from 'react';
import UnitTestsGenerationForm, {
  CodingUnitTestsGenerationConfig,
} from './UnitTestsGenerationForm';
import RunHiddenTestsButton from 'react-scale/components/scalers/tasks/textcollection/SimpleTextCollection/RunHiddenTestsButton';

interface TextCollectionFormProps {
  title?: string;
  description?: string;
  fields: TextCollectionField[];
  annotations: Record<string, TextCollectionAnnotation>;
  setAnnotations: (annotations: TextCollectionAnnotationsOrCallback) => void;
  readOnly: boolean;
  assignmentId?: string;
  chatTaskBehaviorLogMetadata?: ChatTaskBehaviorLogMetadata;
  setSubmitCodeError?: (field_id: string, error: string) => void;
  readOnlyMessage?: string;
  templateVariables?: Record<string, string>;
  chatMessageDisplayConfig?: ChatMessageDisplayConfig;
  codingUnitTestsGenerationConfig?: CodingUnitTestsGenerationConfig;
  projectId?: string;
  lastPrompt?: string | null;
  lastModelResponseEditorContent?: string | null;
  enableRTLText?: boolean;
}

export default function TextCollectionForm({
  setAnnotations,
  annotations,
  fields,
  ...props
}: TextCollectionFormProps) {
  annotations = annotations || {};
  const templateVariables = props.templateVariables ?? useTemplateVariableStore.getState();

  const unitTestsGenerationEnabled =
    props.codingUnitTestsGenerationConfig?.enableCodingUnitTestsGeneration;
  const runHiddenTestsEnabled = props.codingUnitTestsGenerationConfig?.enableRunHiddenTests;
  const unitTestsFieldId = props.codingUnitTestsGenerationConfig?.unitTestsFieldId;
  const codeFieldId = props.codingUnitTestsGenerationConfig?.codeFieldId;
  const [count, setCount] = useState(0);

  const fieldSections = fields
    .filter(field => {
      if (!annotations[field.field_id]) {
        // resolves Cannot add property, object is not extensible error
        annotations = _.cloneDeep(annotations);
        annotations[field.field_id] = getDefaultAnnotation(field);
      }
      // Skip making FieldSection if the field's conditions are not met
      if (
        field.conditions &&
        !areAllFieldConditionsMet(
          field.conditions,
          flattenAnnotations(annotations),
          Object.values(flattenFields(fields)),
        )
      ) {
        return;
      }
      if (field.conditionsCodeStr && !isFieldConditionsCodeMet(field, fields, annotations)) {
        return false;
      }

      if (
        field.templateVariableConditions &&
        templateVariables &&
        !areTemplateVariableConditionsMet(field, templateVariables)
      ) {
        return false;
      }

      return true;
    })
    .map(field => {
      if (!annotations[field.field_id]) {
        annotations[field.field_id] = getDefaultAnnotation(field);
      }

      const annotation = annotations[field.field_id];
      const setAnnotation = (newAnnotation: TextCollectionAnnotation) => {
        if (newAnnotation.type === TextCollectionFieldType.Tool) {
          const prefilledAnnotations = Object.entries(newAnnotation.prefillFieldIds ?? {}).reduce(
            (acc, [chatTaskFieldId, response]) => {
              const fieldType = fields.find(field => field.field_id === chatTaskFieldId)?.type;
              if (fieldType) {
                let annotationResponse;
                switch (fieldType) {
                  case TextCollectionFieldType.Category:
                    annotationResponse = [[response]];
                    break;
                  case TextCollectionFieldType.AudioRecording:
                    // TODO: this is a hack to be able to import audio recording from tool -- we should refactor to let other mime types work
                    annotationResponse = [
                      {
                        url: response,
                        mimeType: 'audio/webm',
                        name: 'preview.webm',
                      },
                    ];
                    break;
                  default:
                    annotationResponse = [response];
                    break;
                }
                const createdAnnotationFromPrefill: any = {
                  type: fieldType,
                  field_id: chatTaskFieldId,
                  response: annotationResponse,
                };
                acc[chatTaskFieldId] = createdAnnotationFromPrefill;
              }
              return acc;
            },
            {} as Record<string, TextCollectionAnnotation>,
          );
          setAnnotations(annotations => ({
            ...annotations,
            ...prefilledAnnotations,
            [field.field_id]: { ...newAnnotation, prefillFieldIds: undefined },
          }));
        } else {
          setAnnotations(annotations => ({
            ...annotations,
            [field.field_id]: newAnnotation,
          }));
        }
      };

      return (
        <div key={field.field_id}>
          <SimpleFieldSection
            key={count}
            readOnly={props.readOnly}
            field={field}
            annotation={annotation}
            currentAnnotations={annotations}
            setAnnotation={setAnnotation}
            assignmentId={props.assignmentId}
            setSubmitCodeError={props.setSubmitCodeError}
            chatTaskBehaviorLogMetadata={props.chatTaskBehaviorLogMetadata}
            readOnlyMessage={props.readOnlyMessage}
            chatMessageDisplayConfig={props.chatMessageDisplayConfig}
            enableRTLText={props.enableRTLText}
          />

          {runHiddenTestsEnabled && codeFieldId === field.field_id && (
            <RunHiddenTestsButton
              codingUnitTestsGenerationConfig={props.codingUnitTestsGenerationConfig}
              annotations={annotations}
              assignmentId={props.assignmentId}
              field={field}
              setSubmitCodeError={props.setSubmitCodeError}
            />
          )}

          {unitTestsGenerationEnabled && unitTestsFieldId === field.field_id && (
            <UnitTestsGenerationForm
              codingUnitTestsGenerationConfig={props.codingUnitTestsGenerationConfig}
              annotations={annotations}
              setAnnotations={setAnnotations}
              projectId={props.projectId}
              assignmentId={props.assignmentId}
              lastPrompt={props.lastPrompt}
              lastModelResponseEditorContent={props.lastModelResponseEditorContent}
              count={count}
              setCount={setCount}
              field={field}
              setSubmitCodeError={props.setSubmitCodeError}
            />
          )}
        </div>
      );
    });

  return (
    <div className="flex flex-col items-stretch w-full h-full">
      <div className="p-2">{fieldSections}</div>
    </div>
  );
}
